import { forwardRef } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Icon, IconsType } from "./Icon";
import type { TRule, TOption } from "../hooks";
import { Tooltip } from "./Tooltip";
import { BOLD_TEXT, COLOR_BACKGROUND, COLOR_BORDER, COLOR_TEXT, SIZE_TEXT, WIDTH } from "../features/foundation";
import {
    InputText,
    InputNumber,
    InputPassword,
    Textarea,
    Select,
    Checkbox,
    Radio,
    InputDate,
    InputTime,
    InputDatetime,
    InputDaterangeProps,
    InputDaterange,
    InputTimerange,
    InputCode,
    InputBoolean,
    InputFile,
    ControllerWrapper,
    InputDatetimerange,
} from "./_";

import type { InputTextProps, InputNumberProps } from "./_";
import { SEditor } from "@/comn/components";
import { comnUtils } from "../utils";
import { InputCode2 } from "./_/InputCode.v2";
import { InputCodeMulti } from "./_/InputCodeMulti";

export type FormControlType =
    | "text"
    | "number"
    | "password"
    | "select"
    | "radio"
    | "checkbox"
    | "textarea"
    | "date"
    | "time"
    | "datetime"
    | "daterange"
    | "timerange"
    | "datetimerange"
    | "code"
    | "code2"
    | "codemulti"
    | "file"
    | "boolean"
    | "editor";

type TButton = {
    icon?: IconsType;
    onClick?: (arg: any) => void;
};

/* 공통타입
 *
 */
export type FormControlCommonProps = {
    key?: string;
    type?: FormControlType;
    edit?: boolean;
    mode?: "edit" | "view" | null;
    rightText?: string;
    leftButton?: TButton;
    rightButton?: TButton;
    copy?: boolean;
    align?: "start" | "left" | "center" | "right" | "end";

    truncate?: boolean;

    /* select, text */
    as?: "link";
    to?: string;

    name?: string;
    value?: any;
    readOnly?: boolean | ((...args: any) => boolean);
    disabled?: boolean;
    defaultValue?: any;
    maxLength?: number;
    placeholder?: string;
    onClick?: (arg?: any) => void;
    onBlur?: (arg?: any) => void;
    onFocus?: (arg?: any) => void;
    onChange?: (arg?: any) => void;
    onEnter?: (arg?: any) => void;

    invalid?: any;
    size?: keyof typeof WIDTH;
    control?: any;
    message?: string;

    /** style */
    color?: keyof typeof COLOR_TEXT;
    editColor?: keyof typeof COLOR_TEXT;
    borderColor?: keyof typeof COLOR_BORDER;
    bold?: keyof typeof BOLD_TEXT;
    editBold?: keyof typeof BOLD_TEXT;
    fontSize?: keyof typeof SIZE_TEXT;
    backgroundColor?: keyof typeof COLOR_BACKGROUND;
};

type OptionsProps = {
    viewType?: "label" | "value" | "both";
    editType?: string;
    area?: string;
    comnCd?: string;
    options?: TOption[] | string[];
    excludes?: string[] | ((...args: any) => any);
    includes?: { label: string; value: string }[];
    filter?: (...arg: any) => boolean;
    callback?: (...arg: any) => any;
    asyncParams?: any;
};

type CheckboxProps = {
    all?: boolean;
    checkAll?: boolean;
};

type SelectProps = {
    select?: boolean;
    selectText?: string;
    allText?: string;
};

type RangeProps = {
    start?: any;
    end?: any;
};

type EditorProps = {
    height?: number;
};

type TextareaProps = {
    rows?: number;
};

type DateProps = {
    startRef?: any;
    endRef?: any;
    second?: boolean;
    maxDate?: Date;
    minDate?: Date;
};

type CodeProps = {
    /** code */
    popupSize?: "sm" | "md" | "lg";
    popupParams?: any;
    disableLabel?: boolean;
    disableSearch?: boolean;
    readOnlyInput?: boolean;
    onBeforeClickSearch?: (arg?: any) => boolean;
    async?: any;
};

type FileProps = {
    multiple?: boolean;
    bsopClsfCd?: string;
    readOnlyName?: boolean | "origin";
    readOnlyTitle?: boolean | "origin";
    readOnlyDescription?: boolean | "origin";
    disableHeader?: boolean;
    disableName?: boolean;
    disableTitle?: boolean;
    disableDescription?: boolean;
    accept?: string;
    thumbnail?: boolean;
    thumbnailPlaceholder?: boolean;
    thumbnailAlign?: "left" | "center" | "right";
    thumbnailHeight?: string | number;
    thumbnailWidth?: string | number;
    valueType?: "file";
    pdfViewer?: boolean;
    isPublic?: boolean;
    disableDelete?: boolean;
    disableRemoveButton?: boolean;

    allow?: string | string[];
};

type BooleanProps = {
    inputLabel?: string;
    trueLabel?: string;
    falseLabel?: string;
    trueValue?: any;
    falseValue?: any;
};

export type FormControlProps =
    /* Common */
    FormControlCommonProps &
        /* InputText */
        InputTextProps &
        /* InputNumber */
        InputNumberProps &
        /* InputTextarea */
        TextareaProps &
        /* InputCheckbox */
        CheckboxProps &
        /* InputSelect */
        SelectProps &
        /* InputCode */
        CodeProps &
        /* InputDate, InputTime */
        DateProps &
        /* InputDaterange, InputTimerange  */
        InputDaterangeProps &
        RangeProps &
        /* InputFile */
        FileProps &
        /* InputBoolean */
        BooleanProps &
        /* Editor */
        EditorProps &
        /* Options */
        OptionsProps &
        /* Rule */
        TRule;

const FormControlMain = forwardRef((props: any, ref) => {
    const { type = "text", rightButton, leftButton, rightText, getValues, ...rest } = props;
    const { edit = true } = props;

    return (
        <div data-lb={!!leftButton || undefined} data-rb={!!rightButton || undefined} className="uf-form-control-main">
            {edit && leftButton && (
                <button type="button" onClick={leftButton.onClick} className="uf-left-button">
                    <Icon icon={leftButton.icon} size="xs" />
                </button>
            )}

            <div className="w-full relative flex items-center">
                {(() => {
                    switch (type) {
                        case "text":
                            return <InputText {...rest} ref={ref} />;
                        case "number":
                            return <InputNumber {...rest} rightText={rightText} ref={ref} />;
                        case "password":
                            return <InputPassword {...rest} ref={ref} />;
                        case "textarea":
                            return <Textarea {...rest} ref={ref} />;
                        case "select":
                            return <Select {...rest} ref={ref} />;
                        case "radio":
                            return <Radio {...rest} />;
                        case "file":
                            return <InputFile {...rest} />;
                        case "checkbox":
                            return <Checkbox {...rest} />;
                        case "date":
                            return <InputDate {...rest} ref={ref} />;
                        case "time":
                            return <InputTime {...rest} ref={ref} />;
                        case "datetime":
                            return <InputDatetime {...rest} ref={ref} />;
                        case "daterange":
                            return <InputDaterange {...rest} />;
                        case "timerange":
                            return <InputTimerange {...rest} />;
                        case "datetimerange":
                            return <InputDatetimerange {...rest} />;
                        case "code":
                            return <InputCode2 {...rest} ref={ref} />;
                        case "code2":
                            return <InputCode2 {...rest} ref={ref} />;
                        case "codemulti":
                            return <InputCodeMulti {...rest} ref={ref} />;
                        case "boolean":
                            return <InputBoolean {...rest} />;
                        case "editor":
                            return <SEditor {...rest} />;
                        default:
                            return <InputText {...rest} ref={ref} />;
                    }
                })()}
                {edit && rightText && <span className="absolute right-0 px-1">{rightText}</span>}
                {!edit && rightText && <span className="px-1 min-w-max">{rightText}</span>}
            </div>

            {edit && rightButton && (
                <button type="button" className="uf-right-button" onClick={rightButton.onClick}>
                    <Icon icon={rightButton.icon} size="xs" />
                </button>
            )}
        </div>
    );
});

export const FormControl = forwardRef((props: FormControlProps, ref) => {
    const { size = "full", message, invalid, backgroundColor, ...rest } = props;
    const { type, edit = true, control } = props;

    const { t } = useTranslation();

    return (
        <div
            className={classNames(
                "group/control uf-form-control",
                edit ? WIDTH[size] : type === "editor" || (type === "file" && props.multiple) ? "w-full" : "w-auto",
                backgroundColor && COLOR_BACKGROUND[backgroundColor],
            )}
        >
            <Tooltip enabled={Boolean(invalid)} size="full" content={t(invalid?.message)}>
                {control ? (
                    <ControllerWrapper {...rest}>
                        <FormControlMain />
                    </ControllerWrapper>
                ) : (
                    <FormControlMain ref={ref} {...rest} />
                )}
            </Tooltip>

            {/* message */}
            {edit && message && <div className="uf-message">{message}</div>}
            {/* invalid message */}
            {edit && invalid && (
                <div className="text-sm mt-1 text-error">
                    {/*t(invalid?.message || getInvalidMessage(invalid, props, "invalid"))*/}
                    {comnUtils.getInvalidMessage(t, invalid, props, "invalid")}
                </div>
            )}
        </div>
    );
});
